import PropTypes from 'prop-types';
import { Resource } from './RDFTypes';
import { createSelector } from 'reselect';

export const NAMESPACE = 'pb';

export function findPropType(fn, propTypes = PropTypes) {
  for (let name in propTypes) {
    if (fn === propTypes[name]) {
      return { name, required: false };
    }
    if (fn === propTypes[name].isRequired) {
      return { name, required: true };
    }
  }
  return { name: fn.name || 'anonymous', required: false };
}

export class Component extends Resource {
  constructor(name, options) {
    super(name, options);
    this.typeOf(`${NAMESPACE}:component`);
  }
}

export class PropType extends Resource {
  constructor(name, options = {}) {
    super(name, options);
    const { fn = () => {}, propTypes } = options;
    const { name: propName } = findPropType(fn, propTypes);
    this.property(name);
    this.typeOf(`${NAMESPACE}:${propName}`);
  }
}

export const buildDescriptorMethod = resource => method =>
  createSelector(
    createSelector(
      content => content,
      (content, options) => options,
      (content, options) => ({
        ...(typeof options === 'function' ? { fn: options } : options),
        ...(typeof content !== 'object' ? { content } : content)
      })
    ),
    ({ content, fn, ...options } = {}) =>
      resource
        .clone()
        .content(content)
        [method](fn, options) // eslint-disable-line no-unexpected-multiline
  );

export const buildDescriptor = resource => {
  const builder = buildDescriptorMethod(resource);

  const fn = builder('toProps');
  fn.selector = builder('toSelector');
  fn.triple = builder('toTriple');
  return fn;
};

const mapPropTypes = (propTypes, options) =>
  Object.keys(propTypes)
    .map(
      k =>
        new PropType(k, {
          fn: propTypes[k],
          ...options
        })
    )
    .reduce(
      (o, p) => ({
        ...o,
        [p.getName()]: buildDescriptor(p)
      }),
      {}
    );

export function describeComponent(name, propTypes = {}, options = {}) {
  const component = new Component(name);
  const { meta = {}, ...propTypeOptions } = options;

  return Object.assign(buildDescriptor(component), {
    props: mapPropTypes({ ...propTypes, ...meta }, propTypeOptions)
  });
}

export function content(options) {
  return function(Component) {
    if (typeof Component !== 'function') {
      console.error(
        `Expected Component to be a function, but recieved a ${typeof Component}`
      );
    }

    const displayName = Component.displayName || Component.name;

    if (typeof displayName !== 'string' || displayName.length === 0) {
      console.error(
        `Expected displayName to be a string, but recieved a ${typeof displayName}`
      );
    }

    const propTypes = Component.propTypes;

    class Content extends Component {
      static contentTypes = describeComponent(displayName, propTypes, options);
    }

    Content.displayName = displayName;

    return Content;
  };
}



// WEBPACK FOOTER //
// ./js/util/ContentTypes.js